import clsx from 'clsx'
import { useState } from 'react'
import { useRouter } from 'next/router'
import { Form, Formik, FormikHelpers } from 'formik'

/* blocks */
import Button from '@/atoms/Button/Button'
import TextField from '@/molecules/FormikFields/TextField'
import PhoneField from '@/molecules/FormikFields/PhoneField'
import CheckboxField from '@/molecules/FormikFields/CheckboxField'
import ThankYou from './ThankYou'

/* utils */
import { FormValues, initialFormValues, validationSchema } from './schema'
import { dataLayerFormSubmit } from '@/utils/functions/dataLayerEvents'
import { useToast } from '@/hooks/useToast'

/* styling */
import styles from './_.module.css'

interface Props {
  title: string
  eventType?: string
  eventId?: string
  venue?: string
  eventSlug?: string
  thankYouMessage: string
  recordingUrl?: string
}

function EventsForm({
  title,
  eventType,
  eventId,
  venue,
  eventSlug,
  thankYouMessage,
  recordingUrl
}: Props): JSX.Element {
  const router = useRouter()
  const { toast, showToast } = useToast()
  const [isSuccess, setIsSuccess] = useState(false)

  const submitToMailChimp = async (url: string, values: FormValues) => {
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        ...values,
        formType: eventType,
        eventCity: venue,
        eventSlug,
        eventId,
        sourceUrl: router.asPath,
        status: 'subscribed'
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })

    const data = await response.json()
    return { ok: response.ok, data }
  }

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    try {
      const result = await submitToMailChimp(
        '/api/forms/events-multi-step-form/',
        values
      )
      if (!result.ok) {
        showToast({
          title: result.data.message,
          type: 'error',
          swipeDirection: 'up'
        })
        return
      }
      setIsSuccess(true)
      dataLayerFormSubmit({
        formName: 'Events Form',
        formValues: values
      })
      helpers.resetForm()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div
      id="events-form"
      className="font-body bg-white | border border-blue-web-40 shadow-lg rounded-xl p-7 md:p-14"
    >
      {isSuccess ? (
        ''
      ) : (
        <h3 className="text-xl md:text-3xl font-semibold text-blue-web-90 text-center | mb-12">
          {title}
        </h3>
      )}
      {!isSuccess ? (
        <div>
          <Formik<FormValues>
            initialValues={initialFormValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            enableReinitialize={true}
          >
            {({ isSubmitting }) => (
              <Form name="Events Form" id="events-form">
                <div className="md:flex md:gap-5">
                  <TextField
                    name="firstName"
                    className={clsx('mb-5', styles.formField)}
                    label="First Name"
                    required
                  />
                  <TextField
                    name="lastName"
                    className={clsx('mb-5', styles.formField)}
                    label="Last Name"
                    required
                  />
                </div>
                <div className="md:flex md:gap-5">
                  <TextField
                    name="email"
                    className={clsx('mb-5', styles.formField)}
                    label="Email Address"
                    required
                  />
                  <PhoneField
                    name="phone"
                    className={clsx('text-black mb-5 z-10', styles.formField)}
                    label="Contact Number"
                    required
                  />
                </div>
                <div className="md:flex md:gap-5">
                  <TextField
                    name="company"
                    className={clsx('mb-5', styles.formField)}
                    label="Company Name"
                    required
                  />
                  <TextField
                    name="designation"
                    className={clsx('mb-5', styles.formField)}
                    label="Designation"
                    required
                  />
                </div>
                <div className="flex justify-center align-middle">
                  <CheckboxField
                    name="newsletterCheckbox"
                    label="Subscribe to our Newsletter for news and other updates."
                    labelClass="text-sm md:text-lg text-black"
                    className={clsx('my-8', styles.formFieldCheckbox)}
                  />
                </div>
                <div>
                  <Button
                    className={clsx(
                      'text-xl font-medium | !rounded | w-full',
                      styles.registerBtn
                    )}
                    size="large"
                    htmlType="submit"
                  >
                    {isSubmitting ? 'Loading...' : 'Submit'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
          {toast}
        </div>
      ) : (
        <ThankYou
          eventType={eventType}
          thankYouMessage={thankYouMessage}
          recordingUrl={recordingUrl}
        />
      )}
    </div>
  )
}

export default EventsForm
